import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { Award, User2, ChevronRight, Loader2 } from "lucide-react";
import { api } from "../lib/api";
import Navbar from "../components/Navbar";

export default function JuryProfile() {
  const { slug } = useParams();
  const [jury, setJury] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState(null);

  useEffect(() => {
    setLoading(true);
    api.get(`/jury/${slug}`)
      .then((r) => setJury(r.data))
      .catch((e) => setErr(e?.response?.status === 404 ? "not-found" : "error"))
      .finally(() => setLoading(false));
  }, [slug]);

  if (loading) {
    return (
      <div className="min-h-screen bg-[#0A0A0A] text-white flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-gray-500" />
      </div>
    );
  }

  if (err === "not-found") {
    return (
      <div className="min-h-screen bg-[#0A0A0A] text-white">
        <Navbar />
        <div className="pt-24 px-6 text-center max-w-lg mx-auto">
          <User2 className="w-16 h-16 mx-auto text-gray-600 mb-4" />
          <h1 className="text-2xl font-bold mb-2">Jury member not found</h1>
          <p className="text-sm text-gray-400">This jury profile may not be published, or the link is broken.</p>
          <Link to="/festival" className="text-amber-300 hover:underline text-sm mt-4 inline-block">← Back to Festival</Link>
        </div>
      </div>
    );
  }

  const photo = jury?.photo_url ? (jury.photo_url.startsWith("http") ? jury.photo_url : `${process.env.REACT_APP_BACKEND_URL}${jury.photo_url}`) : null;

  return (
    <div className="min-h-screen bg-[#0A0A0A] text-white">
      <Navbar />
      <div className="pt-16">
        {/* Hero */}
        <div className="relative overflow-hidden">
          <div className="absolute inset-0 bg-gradient-to-br from-[#1a0608] via-[#0A0A0A] to-[#0A0A0A]" />
          <div className="absolute inset-0 opacity-30" style={{ background: "radial-gradient(ellipse 80% 60% at 30% 40%, rgba(245,200,80,0.25) 0%, transparent 60%)" }} />
          <div className="relative max-w-5xl mx-auto px-6 lg:px-12 py-16 flex flex-col md:flex-row gap-8 md:items-end">
            {photo ? (
              <img src={photo} alt={jury.display_name} className="w-40 h-40 md:w-48 md:h-48 rounded-full object-cover border-4 border-amber-300/60 shadow-2xl" />
            ) : (
              <div className="w-40 h-40 md:w-48 md:h-48 rounded-full bg-[#1a0608] border-4 border-amber-300/60 flex items-center justify-center text-6xl font-black text-[#E50914] shadow-2xl">
                {(jury.display_name || "?").charAt(0).toUpperCase()}
              </div>
            )}
            <div className="flex-1 min-w-0">
              <div className="text-xs uppercase tracking-[0.3em] text-amber-300 mb-2">World Film Festival · Jury Member</div>
              <h1 data-testid="jury-name" className="text-4xl md:text-6xl font-black tracking-tight">{jury.display_name}</h1>
              {jury.bio && <p className="text-lg text-gray-300 mt-4 leading-relaxed max-w-2xl">{jury.bio}</p>}
            </div>
          </div>
        </div>

        {/* Achievements */}
        {jury.achievements?.length > 0 && (
          <div className="max-w-5xl mx-auto px-6 lg:px-12 py-12">
            <div className="text-xs uppercase tracking-[0.25em] text-amber-300 mb-3 flex items-center gap-2"><Award className="w-4 h-4" /> Achievements</div>
            <ul data-testid="jury-achievements" className="grid md:grid-cols-2 gap-3">
              {jury.achievements.map((a, i) => (
                <li key={i} className="pl-4 border-l-2 border-amber-300/40 text-gray-300 leading-relaxed">{a}</li>
              ))}
            </ul>
          </div>
        )}

        {/* Films judged */}
        {jury.films_judged?.length > 0 && (
          <div className="max-w-5xl mx-auto px-6 lg:px-12 pb-16">
            <div className="text-xs uppercase tracking-[0.25em] text-amber-300 mb-4">Recently judged</div>
            <div data-testid="jury-films-judged" className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-4">
              {jury.films_judged.map((f) => (
                <FilmThumb key={f.submission_id} film={f} />
              ))}
            </div>
          </div>
        )}

        <div className="max-w-5xl mx-auto px-6 lg:px-12 pb-16">
          <Link to="/festival" className="inline-flex items-center gap-1 text-sm text-gray-400 hover:text-amber-300 transition">
            ← Back to World Film Festival <ChevronRight className="w-3.5 h-3.5" />
          </Link>
        </div>
      </div>
    </div>
  );
}

function FilmThumb({ film }) {
  const poster = film.poster_url ? (film.poster_url.startsWith("http") ? film.poster_url : `${process.env.REACT_APP_BACKEND_URL}${film.poster_url}`) : null;
  const badge = { approve: "bg-emerald-500/80 text-white", reject: "bg-red-500/80 text-white", recommend_tier: "bg-amber-400 text-black" }[film.verdict];
  return (
    <div className="relative">
      {poster ? (
        <img src={poster} alt={film.title} className="w-full aspect-[2/3] rounded-lg object-cover border border-white/10" />
      ) : (
        <div className="w-full aspect-[2/3] rounded-lg bg-[#1a0608] border border-white/10 flex items-center justify-center"><Award className="w-8 h-8 text-amber-300/30" /></div>
      )}
      <span className={`absolute top-2 left-2 text-[9px] font-bold tracking-widest uppercase px-1.5 py-0.5 rounded ${badge}`}>{film.verdict?.replace("_", " ")}</span>
      <p className="text-xs mt-2 truncate">{film.title || "Untitled"}</p>
    </div>
  );
}
